import getFavoriteCategorySuggestions from '../actions/helpers/getFavoriteCategorySuggestions';
import getFavoriteFolderSuggestions from '../actions/helpers/getFavoriteFolderSuggestions';
import getFavoriteGroupSuggestions from '../actions/helpers/getFavoriteGroupSuggestions';
import getFavoritePeopleSuggestions from '../actions/helpers/getFavoritePeopleSuggestions';
import getFavoriteSearchSuggestions from '../actions/helpers/getFavoriteSearchSuggestions';
import FavoriteSuggestion from '../types/FavoriteSuggestion';
import FavoriteSuggestionDataType from '../types/FavoriteSuggestionDataType';
import FavoriteSuggestionType from '../types/FavoriteSuggestionType';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { PersonaSize } from 'office-ui-fabric-react/lib/Persona';
import { logUsage } from 'owa-analytics';
import { CategoryIcon } from 'owa-categories';
import { ControlIcons } from 'owa-control-icons';
import {
    lazyAddFavoriteFolder,
    showFindFavoritesPicker,
    lazyHandleToggleFavoritePersonaError,
} from 'owa-mail-favorites-store';
import { lazyAddFavoriteCategory, lazyAddFavoriteGroup } from 'owa-favorites';
import { IsGroupsEnabled } from 'owa-group-feature-flags';
import addFavoriteSearch from 'owa-mail-favorites-store/lib/actions/v1/addFavoriteSearch';
import addFavoritePersona from 'owa-mail-favorites-store/lib/actions/addFavoritePersona';
import { isFeatureEnabled } from 'owa-feature-flags';
import { setFindText } from 'owa-mail-find-favorites-store';
import { findFavoritesStore } from 'owa-mail-find-favorites-store';
import { resetFocus } from 'owa-mail-focus-manager';
import { getCustomIcon } from 'owa-mail-folder-forest-actions';
import PersonaControl from 'owa-persona/lib/components/PersonaControl';
import PersonaType from 'owa-service/lib/contract/PersonaType';
import strings from 'owa-strings/owa-mail-strings';
import * as React from 'react';
import {
    BasePickerListBelow,
    IBasePickerProps,
    IBasePicker,
} from 'office-ui-fabric-react/lib/Pickers';
import { lazyCreateNewPdlFavoriteClickedAction } from 'owa-favorite-pdl-editor';

const styles = require('./FavoritesPicker.scss');

export class FavoritesPickerInternal extends BasePickerListBelow<
    FavoriteSuggestion,
    IBasePickerProps<FavoriteSuggestion>
> {
    constructor(props: IBasePickerProps<FavoriteSuggestion>) {
        super(props);
        this.onInputBlur = this.onInputBlur.bind(this);
    }

    /**
     * Overrides base onInputBlur to be able to hide entire component
     * if the input loses focus without any suggestions visible.
     */
    protected onInputBlur = (ev: React.FocusEvent<HTMLInputElement>) => {
        if (!this.state.suggestionsVisible) {
            showFindFavoritesPicker(false);
        }
    };
}

export interface FavoritesPickerProps {
    onItemSelected: (displayName: string) => void;
}

@observer
export default class FavoritesPicker extends React.Component<FavoritesPickerProps, {}> {
    private picker: IBasePicker<FavoriteSuggestion>;

    componentDidMount() {
        this.picker.focusInput();
    }

    render() {
        return (
            <div className={styles.favoritesPickerContainer} tabIndex={-1}>
                <FavoritesPickerInternal
                    className={styles.favoritesPicker}
                    componentRef={(picker: IBasePicker<FavoriteSuggestion>) =>
                        (this.picker = picker)
                    }
                    inputProps={{
                        placeholder: strings.searchForFavoriteText,
                    }}
                    onDismiss={() => this.dismissFavoritesPicker()}
                    onEmptyInputFocus={() => this.onEmptyInputFocus()}
                    onItemSelected={(item: FavoriteSuggestion) => this.onItemSelected(item)}
                    onRenderSuggestionsItem={(item: FavoriteSuggestion) =>
                        this.onRenderSuggestionsItem(item)
                    }
                    onRenderItem={() => this.onRenderItem()}
                    onResolveSuggestions={(findText: string) => this.onFindTextChanged(findText)}
                    pickerSuggestionsProps={{
                        className: styles.favoritesPickerSuggestions,
                        noResultsFoundText: strings.noFavoritesFoundText,
                        suggestionsItemClassName: styles.favoritesPickerSuggestionsItem,
                    }}
                />
            </div>
        );
    }

    /**
     * This function dismisses the picker.
     */
    private dismissFavoritesPicker() {
        showFindFavoritesPicker(false);
    }

    /**
     * This function is called when the input first gets focused in order
     * to provide an initial list of suggestions.
     */
    private onEmptyInputFocus() {
        return this.onFindTextChanged('');
    }

    /**
     * This function both sets findText in the findFavoritesStore (to maintain
     * store's fidelity) and retrieves matching suggestions.
     * @param findText The text in the picker
     */
    private onFindTextChanged(findText: string) {
        /**
         * Defensive check to ensure findText is non-null. Bug #26271 outlines
         * minimal cases in which this value is somehow null.
         */
        if (findText === null) {
            findText = '';
        }

        setFindText(findText);

        return this.getFavoriteSuggestions(findText);
    }

    /**
     * The is the callback for when a user selects a suggestion from the
     * picker.
     * @param item The item selected from the picker
     */
    private onItemSelected = async (item: FavoriteSuggestion) => {
        const { data, id, type } = item;

        const suggestionType: FavoriteSuggestionType = type;

        let favoriteDisplayName = '';

        /**
         * Based on the type of suggestion, call the appropriate function to
         * add it to the list of user's favorites.
         */
        switch (suggestionType) {
            case FavoriteSuggestionType.Folder:
                const addFavoriteFolder = await lazyAddFavoriteFolder.import();
                addFavoriteFolder(id, 'FavoritesPicker');
                favoriteDisplayName = data as string;
                break;
            case FavoriteSuggestionType.Persona:
                const personaType: PersonaType = data as PersonaType;
                addFavoritePersona(
                    undefined,
                    personaType.EmailAddress.EmailAddress,
                    personaType.DisplayName,
                    'FavoritesPicker'
                ).catch(() => lazyHandleToggleFavoritePersonaError.importAndExecute(false));
                favoriteDisplayName = personaType.DisplayName;
                break;
            case FavoriteSuggestionType.Search:
                addFavoriteSearch(data as string, 'FavoritesPicker');
                favoriteDisplayName = data as string;
                break;

            case FavoriteSuggestionType.Category:
                const addFavoriteCategory = await lazyAddFavoriteCategory.import();
                addFavoriteCategory(id, 'FavoritesPicker');
                favoriteDisplayName = data as string;
                break;

            case FavoriteSuggestionType.Group:
                const addFavoriteGroup = await lazyAddFavoriteGroup.import();
                addFavoriteGroup(id, data as string, 'FavoritesPicker');
                favoriteDisplayName = data as string;
                break;

            case FavoriteSuggestionType.NewPdl:
                favoriteDisplayName = strings.newContactListButtonText;
                (await lazyCreateNewPdlFavoriteClickedAction.import())();
                break;
        }

        /**
         * Hide the favorites picker and reset focus.
         */
        showFindFavoritesPicker(false);
        resetFocus();

        logUsage('TnS_AddFavoriteFromPicker', [findFavoritesStore.findText.length, suggestionType]);

        this.props.onItemSelected(favoriteDisplayName);

        return item;
    };

    /**
     * This function is used by the BasePicker to render a suggestion in the
     * picker.
     * @param item The suggestion to be rendered
     */
    private onRenderSuggestionsItem(item: FavoriteSuggestion) {
        return <div>{this.renderFavoriteSuggestion(item.id, item.type, item.data)}</div>;
    }

    /**
     * The typical use case of a Fabric picker is to input text, provide the user
     * with suggestions, allow them to select a suggestion, and then show their
     * selection within the picker. Because we're taking the user's selection and
     * immediately performing an action with it, there's no opportunity to render
     * a selected suggestion. However, OUFR BasePicker throws an exception without
     * this prop (even though it's optional).
     */
    private onRenderItem() {
        return null;
    }

    /**
     * This is a helper function that retrieves a list of matching suggestions
     * based on the input provided to the picker.
     * @param findText The text in the picker
     */
    private getFavoriteSuggestions(findText: string): FavoriteSuggestion[] {
        const isRoamingFavoritesAPISupported = isFeatureEnabled('tri-favorites-roaming');
        const favoriteFolderSuggestions = getFavoriteFolderSuggestions(findText);
        const favoritePeopleSuggestions = getFavoritePeopleSuggestions(findText);
        let favoriteGroupSuggestions;

        if (isRoamingFavoritesAPISupported) {
            // VSO 26190, 26191: [Prime]Support favorite Persona, Search and Category type
            favoriteGroupSuggestions = IsGroupsEnabled()
                ? getFavoriteGroupSuggestions(findText)
                : [];
        } else {
            favoriteGroupSuggestions = [];
        }

        const favoriteSearchSuggestions =
            isFeatureEnabled('tri-favoriteSearch') && !isFeatureEnabled('tri-favorites-roaming')
                ? getFavoriteSearchSuggestions(findText)
                : [];
        const favoriteCategorySuggestions = getFavoriteCategorySuggestions(findText);

        const newFavoritePdlSuggestion =
            isFeatureEnabled('peo-favoritePdls') && !findText
                ? [
                      {
                          id: 'newPdl',
                          type: FavoriteSuggestionType.NewPdl,
                      },
                  ]
                : [];

        const favoriteSuggestions: FavoriteSuggestion[] = [
            ...favoritePeopleSuggestions,
            ...favoriteFolderSuggestions,
            ...favoriteSearchSuggestions,
            ...favoriteCategorySuggestions,
            ...favoriteGroupSuggestions,
            ...newFavoritePdlSuggestion,
        ];

        logUsage('TnS_ShowFavoriteSuggestions', [
            findText.length,
            favoritePeopleSuggestions.length,
            favoriteFolderSuggestions.length,
            favoriteSearchSuggestions.length,
            favoriteCategorySuggestions.length,
            favoriteGroupSuggestions.length,
        ]);

        return favoriteSuggestions;
    }

    /**
     * This is a helper render function that calls into another helper render
     * function based on the selected suggestion's type.
     */
    private renderFavoriteSuggestion(
        id: string,
        type: FavoriteSuggestionType,
        data: FavoriteSuggestionDataType
    ): JSX.Element {
        switch (type) {
            case FavoriteSuggestionType.Folder:
                return this.renderFavoriteFolderSuggestion(id, data as string);
            case FavoriteSuggestionType.Persona:
                return this.renderFavoritePersonaSuggestion(id, data as PersonaType);
            case FavoriteSuggestionType.Search:
                return this.renderFavoriteSearchSuggestion(id, data as string);
            case FavoriteSuggestionType.Category:
                return this.renderFavoriteCategorySuggestion(id, data as string);
            case FavoriteSuggestionType.Group:
                return this.renderFavoriteGroupSuggestion(id, data as string);
            case FavoriteSuggestionType.NewPdl:
                return this.renderNewPdlSuggestion();
        }

        return null;
    }

    /**
     * This function is used to render a category suggestion.
     */
    private renderFavoriteCategorySuggestion(id: string, name: string): JSX.Element {
        return (
            <div className={styles.suggestionWrapper}>
                <CategoryIcon categoryName={name} iconClassName={styles.favoriteIcon} />
                <span className={styles.suggestionDisplayName}>{name}</span>
            </div>
        );
    }

    /**
     * This function is used to render a search suggestion.
     */
    private renderFavoriteSearchSuggestion(id: string, data: string): JSX.Element {
        return (
            <div className={styles.suggestionWrapper}>
                <Icon className={styles.favoriteIcon} iconName={ControlIcons.Search} />
                <span className={styles.suggestionDisplayName}>{data}</span>
            </div>
        );
    }

    /**
     * This function is used to render a person suggestion.
     */
    private renderFavoritePersonaSuggestion(id: string, data: PersonaType): JSX.Element {
        const personaType: PersonaType = data;
        return (
            <div className={styles.suggestionWrapper}>
                <PersonaControl
                    className={styles.persona}
                    name={personaType.EmailAddress.Name}
                    emailAddress={personaType.EmailAddress.EmailAddress}
                    size={PersonaSize.size24}
                />
                <span className={styles.suggestionDisplayName}>
                    {personaType.EmailAddress.Name}
                </span>
            </div>
        );
    }

    private renderNewPdlSuggestion(): JSX.Element {
        return (
            <div className={styles.suggestionWrapper}>
                <Icon className={styles.favoriteIcon} iconName={ControlIcons.Add} />
                <span className={styles.suggestionDisplayName}>
                    {strings.newContactListButtonText}
                </span>
            </div>
        );
    }

    /**
     * This function is used to render a folder suggestion.
     */
    private renderFavoriteFolderSuggestion(id: string, data: string): JSX.Element {
        const folderIcon = getCustomIcon(id, true /* showNonSpecialFolderIcon */);

        return (
            <div className={styles.suggestionWrapper}>
                <Icon className={styles.favoriteIcon} iconName={folderIcon} />
                <span className={styles.suggestionDisplayName}>{data}</span>
            </div>
        );
    }

    /**
     * This function is used to render a Group suggestion.
     */
    private renderFavoriteGroupSuggestion(id: string, name: string): JSX.Element {
        return (
            <div className={styles.suggestionWrapper}>
                <Icon className={styles.favoriteIcon} iconName={ControlIcons.People} />
                <span className={styles.suggestionDisplayName}>{name}</span>
            </div>
        );
    }
}
